import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import classNames from "classnames";
import * as React from "react";

import { Draggable, DragItem } from "@/shared/components/visualization/DnD";
import { Expandable } from "@/shared/components/visualization/Expandable";
import { type MessagePathNode } from "@/shared/domain/topics";

import { TopicTreeNode } from "./buildTopicTree";
import { ContextMenu } from "./ContextMenu";
import styles from "./MessagePath.module.css";
import { MessagePathLabel } from "./MessagePathLabel";
import { ShowHiddenToggle } from "./ShowHiddenToggle";
import { useExpandable, useVisibleChildren } from "./TreeNodeHooks";

interface MessagePathProps {
  node: TopicTreeNode<MessagePathNode>;
}

/**
 * Render a MessagePath and its sub-MessagePaths (if any).
 */
export function MessagePath(props: MessagePathProps) {
  const { node } = props;
  const { data: messagePathNode } = node;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const { expanded, setExpanded } = useExpandable(node.showChildren);

  const {
    showHidden,
    setShowHidden,
    numHidden,
    children: visibleChildren,
    showHiddenChildrenToggle,
  } = useVisibleChildren(node.children);

  /**
   * Memoizing children because re-rendering MessagePaths is expensive. The ContextMenu
   * component makes an expensive sharesRepresentationWithBinaryField check on every render.
   */
  const children = React.useMemo(
    function renderNestedPaths() {
      return visibleChildren.map((child) => (
        <MessagePath key={child.data.id} node={child} />
      ));
    },
    [visibleChildren],
  );

  const toggleExpanded = () => {
    if (node.children.length === 0) {
      return;
    }

    setExpanded(!expanded);

    if (expanded && !node.showChildren) {
      // Reset the option to show hidden children when the user collapses the node
      // and the node doesn't have a reason to show children
      setShowHidden(false);
    } else if (numHidden === node.children.length) {
      // Show all children if user expands the node when all children are filtered out
      setShowHidden(true);
    }
  };

  const onRightClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setAnchorEl(e.currentTarget);
  };

  const closeMenu = () => {
    setAnchorEl(null);
  };

  const baseLabel = (
    <div
      role="button"
      aria-controls="context-menu"
      aria-label={messagePathNode.data.source_path}
      title={messagePathNode.data.source_path}
      onContextMenu={onRightClick}
      className={classNames("ph-no-capture", styles.truncate)}
    >
      <MessagePathLabel
        dataType={messagePathNode.data.data_type.split("/").pop() || ""}
        label={messagePathNode.label}
        match={node.match}
      />
    </div>
  );

  const label =
    node.children.length === 0 ? (
      <>
        <Draggable<MessagePathNode>
          id={messagePathNode.id}
          item={{ type: DragItem.MessagePath, data: messagePathNode }}
          className={styles.draggable}
        >
          {baseLabel}
        </Draggable>
        <ContextMenu
          node={messagePathNode}
          anchorElement={anchorEl}
          closeMenu={closeMenu}
        />
      </>
    ) : (
      <Draggable<MessagePathNode>
        id={`tree-${messagePathNode.id}`}
        item={{ type: DragItem.TreeNode, data: messagePathNode }}
        className={styles.draggable}
      >
        <Expandable<TopicTreeNode<MessagePathNode>>
          data={node}
          EndIcon={expanded ? ExpandLessIcon : ExpandMoreIcon}
          onClick={toggleExpanded}
          className={styles.reducedWidth}
          iconClassName={styles.expandIcon}
        >
          {baseLabel}
        </Expandable>
      </Draggable>
    );

  return (
    <div>
      {label}
      {expanded && (
        <div className={styles.subPathsContainer}>
          {children}
          {showHiddenChildrenToggle && (
            <ShowHiddenToggle
              numHidden={numHidden}
              showHidden={showHidden}
              onToggle={() => setShowHidden(!showHidden)}
            />
          )}
        </div>
      )}
    </div>
  );
}
